import React, { useState, useEffect, useRef } from 'react';
import './ContactSheet.css';

const ContactModal = ({ isOpen, onClose, initialSubject = '' }) => {
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [gotcha, setGotcha] = useState(''); // honeypot — humans never see or fill this
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const dialogRef = useRef(null);
    const requestId = useRef(0);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!isOpen) return;
        requestId.current += 1;
        setSubject(initialSubject);
        setBody(initialSubject ? 'Please delete my KACHNG account and associated personal, receipt, and purchase information.' : '');
        setSubmitStatus(null);
        setIsSubmitting(false);
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.querySelector('#email')?.focus();
        return () => {
            requestId.current += 1;
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    }, [isOpen, initialSubject]);

    // Close on Escape and keep keyboard focus inside the dialog.
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') onCloseRef.current();
            if (e.key === 'Tab') {
                const controls = dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled):not([tabindex="-1"]), textarea:not(:disabled), a[href]');
                if (!controls?.length) return;
                const first = controls[0], last = controls[controls.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const activeRequest = requestId.current;
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await fetch('https://formspree.io/f/xldpbywj', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: email,
                    _subject: subject ? `KACHNG: ${subject}` : 'KACHNG contact form',
                    message: body,
                    _gotcha: gotcha
                })
            });

            if (activeRequest !== requestId.current) return;
            if (response.ok) {
                setSubmitStatus('success');
                setEmail('');
                setSubject('');
                setBody('');
                setGotcha('');

            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            if (activeRequest === requestId.current) setSubmitStatus('error');
        } finally {
            if (activeRequest === requestId.current) setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div
                className="modal-content"
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-modal-title"
            >
                <div className="modal-header">
                    <h2 id="contact-modal-title">{initialSubject || 'Talk to the founder'}</h2>
                    <button className="close-button" onClick={onClose} aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="contact-form">
                    {/* Honeypot — hidden from humans, catches bots. Formspree
                        discards any submission where _gotcha is filled. */}
                    <input
                        type="text"
                        name="_gotcha"
                        value={gotcha}
                        onChange={e => setGotcha(e.target.value)}
                        style={{ display: 'none' }}
                        tabIndex="-1"
                        autoComplete="off"
                        aria-hidden="true"
                    />

                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="subject">Subject</label>
                        <input
                            type="text"
                            id="subject"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            placeholder="What's this about?"
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="body">Message</label>
                        <textarea
                            id="body"
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            placeholder="Bug, idea, complaint — all of it goes to the same place."
                            rows="6"
                            required
                            disabled={isSubmitting}
                        ></textarea>
                    </div>

                    {submitStatus === 'success' && (
                        <div className="status-message success" role="status">
                            {initialSubject ? 'Your deletion request has been sent. KACHNG will follow up about verification and the outcome. Your account has not been deleted yet.' : 'Your message has been sent. Thank you for contacting KACHNG.'}
                        </div>
                    )}

                    {submitStatus === 'error' && (
                        <div className="status-message error" role="alert">
                            We could not send your message. Please try again or email us directly:{' '}
                            <a href="mailto:info@kachngco.com">info@kachngco.com</a>
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="submit" className="submit-button" disabled={isSubmitting}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z"/>
                            </svg>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactModal;